const AUTHENTICATION_MESSAGES = [
  'unauthenticated',
  'unauthorized',
  'session is not active',
  'token is expired',
  'invalid token',
  'invalid refresh token'
]

type GraphQLErrorLike = {
  message?: string
  extensions?: Record<string, unknown>
}

// A gRPC error relayed from a service BEHIND the BFF, e.g.
//   rpc error: code = Unauthenticated desc = 0090106001: unauthenticated
//
// This says a backend service could not authenticate to ANOTHER backend service.
// It says nothing about the caller's session, and it must never be read as one.
//
// The distinction is not cosmetic — it decides whether the user stays logged in:
//
//   - The BFF rejects an invalid session with an HTTP 401 (`http.Error(w,
//     "unauthorized", http.StatusUnauthorized)` in authUser_middleware.go), which
//     Apollo surfaces as a networkError. That is the real expiry signal.
//   - A resolver whose downstream call fails returns HTTP 200 with a GraphQL error
//     carrying the relayed `rpc error:` text. The session is perfectly valid.
//
// Observed on staging 2026-08-23: `notificationUnreadCount` failed because the BFF
// sends the access token under gRPC metadata key `token` while notification-service
// read `authorization`. The same cookie returned 200 with data for TicketBadgeCount
// in the same page load. Every login therefore ended in an immediate logout, and the
// cause looked like an auth outage rather than one broken resolver — the substring
// match below turned a missing unread badge into "staging is unusable".
const RELAYED_RPC_ERROR = /\brpc error:/i

export function isAuthenticationError(
  graphQLErrors: readonly GraphQLErrorLike[] | undefined,
  networkError: { message?: string } | null | undefined
): boolean {
  return Boolean(
    graphQLErrors?.some((error) => {
      const rawMessage = error.message || ''
      const message = rawMessage.toLowerCase()
      const code = error.extensions?.code

      // An explicit code is a deliberate signal from our own layer, so it is
      // honoured even on a relayed error: something chose to say UNAUTHENTICATED
      // rather than it being inferred from prose.
      if (code === 'UNAUTHENTICATED' || code === 'UNAUTHORIZED') return true

      // Free-text matching is a heuristic over messages this app does not own, so
      // it is applied ONLY to errors the BFF itself produced. A relayed gRPC error
      // is somebody else's authentication problem.
      if (RELAYED_RPC_ERROR.test(rawMessage)) return false

      return AUTHENTICATION_MESSAGES.some((authMessage) =>
        message.includes(authMessage)
      )
    }) || networkError?.message?.includes('401')
  )
}

export function isLoginRoute(pathname = window.location.pathname): boolean {
  return /\/(en|fa)\/login(?:\/|$)/.test(pathname)
}
